import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useGraph, useSimulationStore, useRunConfig } from '../../../store/simulationStore';
import { useActiveScenario, useScenarioStore, useScenarios } from '../../../store/scenarioStore';
import { useSimulationRunner } from '../../../features/simulation/useSimulationRunner';
import type { DistributionNodeData, SimulationNode } from '../../../models/simulation';
import { parseNumber } from '../../../utils/numberFormatting';
import { useTabNavigation } from '../../../hooks/useTabNavigation';

interface InputDistribution {
  id: string;
  name: string;
  description?: string;
  config: DistributionNodeData['config'];
  baselineConfig: DistributionNodeData['config'];
  isOverridden: boolean;
}

interface ScenarioInputDistributionsPanelProps {
  filteredNodes?: SimulationNode[]; // Optional, not used anymore - kept for compatibility
}

export const ScenarioInputDistributionsPanel = ({ filteredNodes: _filteredNodes }: ScenarioInputDistributionsPanelProps) => {
  const graph = useGraph();
  const runConfig = useRunConfig();
  const { run: runSimulation } = useSimulationRunner();
  const activeScenario = useActiveScenario();
  const scenarios = useScenarios();
  const updateScenarioDistribution = useScenarioStore((state) => state.updateScenarioDistribution);
  const resetScenarioDistribution = useScenarioStore((state) => state.resetScenarioDistribution);
  const setSelection = useSimulationStore((s) => s.setSelection);
  const baselineScenario = useMemo(
    () => scenarios.find((scenario) => {
      const normalizedName = scenario.name.trim().toLowerCase();
      return scenario.isBaseline || normalizedName === 'baseline' || normalizedName === 'baseline scenario' || normalizedName === 'main' || normalizedName === 'main simulation';
    }),
    [scenarios]
  );

  // Get all distribution nodes from graph (not filtered)
  const distributionNodes = useMemo(() => {
    return graph.nodes.filter((node) => node.data.type === 'distribution');
  }, [graph.nodes]);

  // Get configuration for each node (either from scenario override or graph default)
  const getNodeConfig = useCallback((nodeId: string): DistributionNodeData['config'] | undefined => {
    const node = graph.nodes.find((n) => n.id === nodeId);
    if (!node || node.data.type !== 'distribution') return undefined;

    // Check if scenario has an override
    if (activeScenario?.distributionOverrides[nodeId]) {
      return activeScenario.distributionOverrides[nodeId];
    }

    // Use graph default
    return node.data.config;
  }, [graph.nodes, activeScenario]);

  const getBaselineNodeConfig = useCallback((nodeId: string): DistributionNodeData['config'] | undefined => {
    const node = graph.nodes.find((n) => n.id === nodeId);
    if (!node || node.data.type !== 'distribution') return undefined;

    if (baselineScenario?.distributionOverrides[nodeId]) {
      return baselineScenario.distributionOverrides[nodeId];
    }

    return node.data.config;
  }, [graph.nodes, baselineScenario]);

  // Map to input distributions
  const inputDistributions = useMemo<InputDistribution[]>(() => {
    const mapped = distributionNodes.map((node: SimulationNode): InputDistribution => {
      const nodeData = node.data as DistributionNodeData;
      const config = getNodeConfig(node.id) || nodeData.config;
      const isOverridden = !activeScenario?.isBaseline && !!activeScenario?.distributionOverrides[node.id];
      
      return {
        id: node.id,
        name: nodeData.name,
        description: nodeData.description,
        config,
        baselineConfig: getBaselineNodeConfig(node.id) || nodeData.config,
        isOverridden
      };
    });
    mapped.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return mapped;
  }, [distributionNodes, getNodeConfig, getBaselineNodeConfig, activeScenario]);

  const focusNode = useCallback((nodeId?: string) => {
    if (!nodeId) return;
    setSelection(nodeId);
  }, [setSelection]);

  if (inputDistributions.length === 0) {
    return (
      <div className="panel">
        <h2>Input Distributions</h2>
        <p className="hint">Add distribution nodes to define input variables.</p>
      </div>
    );
  }

  return (
    <div className="panel input-distributions-panel">
      <div className="panel-header">
        <h2>Input Distributions</h2>
        <span className="panel-meta">
          {inputDistributions.length} input{inputDistributions.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="panel-body">
        <div className="input-distributions-list">
          {inputDistributions.map((distribution: InputDistribution) => (
            <DistributionCard
              key={distribution.id}
              distribution={distribution}
              onFocus={focusNode}
              onConfigChange={(nodeId, config) => {
                if (activeScenario) {
                  updateScenarioDistribution(activeScenario.id, nodeId, config, graph, runConfig, runSimulation);
                }
              }}
              onReset={(nodeId) => {
                if (activeScenario) {
                  resetScenarioDistribution(activeScenario.id, nodeId, graph, runConfig, runSimulation);
                }
              }}
              isBaseline={activeScenario?.isBaseline || false}
              showDifferences={!(activeScenario?.isBaseline || false)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface DistributionCardProps {
  distribution: InputDistribution;
  onFocus: (nodeId: string) => void;
  onConfigChange: (nodeId: string, config: DistributionNodeData['config']) => void;
  onReset: (nodeId: string) => void;
  isBaseline: boolean;
  showDifferences: boolean;
}

const comparableFields = ['kind', 'value', 'mean', 'std', 'min', 'max', 'mode'] as const;
type ComparableField = (typeof comparableFields)[number];

const DistributionCard = ({ distribution, onFocus, onConfigChange, onReset, isBaseline, showDifferences }: DistributionCardProps) => {
  const registerActiveInput = useSimulationStore((state) => state.registerActiveInput);
  const unregisterActiveInput = useSimulationStore((state) => state.unregisterActiveInput);
  const config = distribution.config;
  const primaryField =
    config.kind === 'fixed'
      ? 'value'
      : config.kind === 'normal'
        ? 'mean'
        : config.kind === 'triangular'
          ? 'mode'
          : null;
  const baselineConfig = distribution.baselineConfig;
  const isFieldDifferent = useCallback((field: ComparableField) => {
    if (!showDifferences) return false;

    switch (field) {
      case 'kind':
        return config.kind !== baselineConfig.kind;
      case 'value':
        return config.kind !== baselineConfig.kind || config.kind !== 'fixed' || baselineConfig.kind !== 'fixed'
          ? config.kind !== baselineConfig.kind || (config.kind === 'fixed' ? config.value : undefined) !== (baselineConfig.kind === 'fixed' ? baselineConfig.value : undefined)
          : config.value !== baselineConfig.value;
      case 'mean':
        return (config.kind === 'normal' ? config.mean : undefined) !== (baselineConfig.kind === 'normal' ? baselineConfig.mean : undefined);
      case 'std':
        return (config.kind === 'normal' ? config.std : undefined) !== (baselineConfig.kind === 'normal' ? baselineConfig.std : undefined);
      case 'min':
        if (config.kind === 'normal') {
          return config.min !== (baselineConfig.kind === 'normal' ? baselineConfig.min : undefined);
        }
        if (config.kind === 'triangular' || config.kind === 'uniform') {
          const baselineMin = baselineConfig.kind === 'triangular' || baselineConfig.kind === 'uniform' ? baselineConfig.min : undefined;
          return config.min !== baselineMin;
        }
        return baselineConfig.kind === 'normal' || baselineConfig.kind === 'triangular' || baselineConfig.kind === 'uniform';
      case 'max':
        if (config.kind === 'normal') {
          return config.max !== (baselineConfig.kind === 'normal' ? baselineConfig.max : undefined);
        }
        if (config.kind === 'triangular' || config.kind === 'uniform') {
          const baselineMax = baselineConfig.kind === 'triangular' || baselineConfig.kind === 'uniform' ? baselineConfig.max : undefined;
          return config.max !== baselineMax;
        }
        return baselineConfig.kind === 'normal' || baselineConfig.kind === 'triangular' || baselineConfig.kind === 'uniform';
      case 'mode':
        return (config.kind === 'triangular' ? config.mode : undefined) !== (baselineConfig.kind === 'triangular' ? baselineConfig.mode : undefined);
      default:
        return false;
    }
  }, [showDifferences, config, baselineConfig]);
  const differenceCount = useMemo(
    () => comparableFields.filter((field) => isFieldDifferent(field)).length,
    [isFieldDifferent]
  );
  const hasDifferences = showDifferences && differenceCount > 0;
  const formatBaselineNumber = useCallback((value: number | undefined) => {
    if (typeof value !== 'number' || !isFinite(value)) return null;
    return String(value);
  }, []);
  const getBaselineNumber = useCallback((field: Exclude<ComparableField, 'kind'>): number | undefined => {
    switch (field) {
      case 'value':
        return baselineConfig.kind === 'fixed' ? baselineConfig.value : undefined;
      case 'mean':
        return baselineConfig.kind === 'normal' ? baselineConfig.mean : undefined;
      case 'std':
        return baselineConfig.kind === 'normal' ? baselineConfig.std : undefined;
      case 'min':
        return baselineConfig.kind === 'normal' || baselineConfig.kind === 'triangular' || baselineConfig.kind === 'uniform'
          ? baselineConfig.min
          : undefined;
      case 'max':
        return baselineConfig.kind === 'normal' || baselineConfig.kind === 'triangular' || baselineConfig.kind === 'uniform'
          ? baselineConfig.max
          : undefined;
      case 'mode':
        return baselineConfig.kind === 'triangular' ? baselineConfig.mode : undefined;
      default:
        return undefined;
    }
  }, [baselineConfig]);
  const renderMainReference = useCallback((field: Exclude<ComparableField, 'kind'>) => {
    if (!showDifferences || !isFieldDifferent(field)) return null;
    const baselineValue = formatBaselineNumber(getBaselineNumber(field));
    if (!baselineValue) return null;
    return <span className="field-baseline-hint">Baseline: {baselineValue}</span>;
  }, [showDifferences, isFieldDifferent, formatBaselineNumber, getBaselineNumber]);

  // Create refs for all input fields in tab order
  const selectRef = useRef<HTMLSelectElement>(null);
  const valueRef = useRef<HTMLInputElement>(null);
  const meanRef = useRef<HTMLInputElement>(null);
  const stdRef = useRef<HTMLInputElement>(null);
  const normalMinRef = useRef<HTMLInputElement>(null);
  const normalMaxRef = useRef<HTMLInputElement>(null);
  const triMinRef = useRef<HTMLInputElement>(null);
  const triModeRef = useRef<HTMLInputElement>(null);
  const triMaxRef = useRef<HTMLInputElement>(null);
  const uniMinRef = useRef<HTMLInputElement>(null);
  const uniMaxRef = useRef<HTMLInputElement>(null);

  // Determine which fields are visible based on distribution kind
  const fieldRefs = useMemo(() => {
    const refs: React.RefObject<HTMLInputElement | HTMLSelectElement>[] = [selectRef];
    
    if (config.kind === 'fixed') {
      refs.push(valueRef);
    } else if (config.kind === 'normal') {
      refs.push(meanRef, stdRef, normalMinRef, normalMaxRef);
    } else if (config.kind === 'triangular') {
      refs.push(triMinRef, triModeRef, triMaxRef);
    } else if (config.kind === 'uniform') {
      refs.push(uniMinRef, uniMaxRef);
    }
    
    return refs;
  }, [config.kind]);

  const { handleKeyDown } = useTabNavigation(fieldRefs);

  const updateConfig = useCallback((next: Partial<typeof config>) => {
    const updatedConfig = { ...config, ...next } as DistributionNodeData['config'];
    onConfigChange(distribution.id, updatedConfig);
  }, [distribution.id, config, onConfigChange]);

  const setKind = useCallback((kind: DistributionNodeData['config']['kind']) => {
    const defaults = (() => {
      switch (kind) {
        case 'fixed':
          return { kind, value: 0 } as const;
        case 'normal':
          return { kind, mean: 0, std: 1 } as const;
        case 'triangular':
          return { kind, min: 0, mode: 0.5, max: 1 } as const;
        case 'uniform':
          return { kind, min: 0, max: 1 } as const;
        default:
          return config;
      }
    })();

    const newConfig = {
      ...defaults,
      label: config.label,
      description: config.description
    } as DistributionNodeData['config'];
    
    onConfigChange(distribution.id, newConfig);
  }, [distribution.id, config, onConfigChange]);

  // Track which input is currently focused to prevent unwanted resets
  const focusedInputRef = useRef<string | null>(null);

  // Local state for all number inputs - using string to allow empty inputs
  const [localValue, setLocalValue] = useState(
    config.kind === 'fixed' ? String(config.value) : '0'
  );
  const [localMean, setLocalMean] = useState(
    config.kind === 'normal' ? String(config.mean) : '0'
  );
  const [localStd, setLocalStd] = useState(
    config.kind === 'normal' ? String(config.std) : '1'
  );
  const [localNormalMin, setLocalNormalMin] = useState(
    config.kind === 'normal' ? String(config.min ?? '') : ''
  );
  const [localNormalMax, setLocalNormalMax] = useState(
    config.kind === 'normal' ? String(config.max ?? '') : ''
  );
  const [localTriMin, setLocalTriMin] = useState(
    config.kind === 'triangular' ? String(config.min) : '0'
  );
  const [localTriMode, setLocalTriMode] = useState(
    config.kind === 'triangular' ? String(config.mode) : '0.5'
  );
  const [localTriMax, setLocalTriMax] = useState(
    config.kind === 'triangular' ? String(config.max) : '1'
  );
  const [localUniMin, setLocalUniMin] = useState(
    config.kind === 'uniform' ? String(config.min) : '0'
  );
  const [localUniMax, setLocalUniMax] = useState(
    config.kind === 'uniform' ? String(config.max) : '1'
  );

  // Sync with external changes when config changes, but only if not focused
  useEffect(() => {
    if (config.kind === 'fixed' && focusedInputRef.current !== 'value') {
      setLocalValue(String(config.value));
    } else if (config.kind === 'normal') {
      if (focusedInputRef.current !== 'mean') setLocalMean(String(config.mean));
      if (focusedInputRef.current !== 'std') setLocalStd(String(config.std));
      if (focusedInputRef.current !== 'normalMin') setLocalNormalMin(String(config.min ?? ''));
      if (focusedInputRef.current !== 'normalMax') setLocalNormalMax(String(config.max ?? ''));
    } else if (config.kind === 'triangular') {
      if (focusedInputRef.current !== 'triMin') setLocalTriMin(String(config.min));
      if (focusedInputRef.current !== 'triMode') setLocalTriMode(String(config.mode));
      if (focusedInputRef.current !== 'triMax') setLocalTriMax(String(config.max));
    } else if (config.kind === 'uniform') {
      if (focusedInputRef.current !== 'uniMin') setLocalUniMin(String(config.min));
      if (focusedInputRef.current !== 'uniMax') setLocalUniMax(String(config.max));
    }
  }, [config]);

  return (
    <div
      className={`input-distribution-card ${distribution.isOverridden ? 'is-overridden' : ''}${hasDifferences ? ' has-differences' : ''}`}
    >
      <div
        className="input-distribution-header"
        onClick={() => onFocus(distribution.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onFocus(distribution.id);
          }
        }}
      >
        <div className="input-distribution-header-content">
          <div>
            <h3 className="input-distribution-name">{distribution.name}</h3>
            {distribution.description && (
              <p className="input-distribution-description">{distribution.description}</p>
            )}
          </div>
          <div className="input-distribution-header-actions">
            {distribution.isOverridden && !isBaseline && (
              <button
                type="button"
                className="scenario-input-reset"
                onClick={(e) => {
                  e.stopPropagation();
                  onReset(distribution.id);
                }}
                title="Reset to graph default"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="input-distribution-fields">
        <label className={`field${isFieldDifferent('kind') ? ' field-different' : ''}`}>
          <span>Distribution</span>
          <select
            ref={selectRef}
            value={config.kind}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              setKind(event.target.value as DistributionNodeData['config']['kind'])
            }
            onKeyDown={(e: KeyboardEvent<HTMLSelectElement>) => handleKeyDown(e, 0)}
            disabled={isBaseline}
          >
            <option value="fixed">Fixed</option>
            <option value="normal">Normal</option>
            <option value="triangular">Triangular</option>
            <option value="uniform">Uniform</option>
          </select>
        </label>

        {config.kind === 'fixed' && (
          <label className={`field${primaryField === 'value' ? ' field-primary' : ''}${isFieldDifferent('value') ? ' field-different' : ''}`}>
            <span>Value</span>
            <input
              ref={valueRef}
              type="number"
              value={localValue}
              onChange={(event) => setLocalValue(event.target.value)}
              onMouseDown={(event) => event.stopPropagation()}
              onFocus={() => {
                focusedInputRef.current = 'value';
                registerActiveInput(`${distribution.id}-value`);
              }}
              onBlur={() => {
                focusedInputRef.current = null;
                unregisterActiveInput(`${distribution.id}-value`);
                updateConfig({ value: parseNumber(localValue, 0) });
              }}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 1)}
              placeholder="Enter value"
              disabled={isBaseline}
            />
            {renderMainReference('value')}
          </label>
        )}

        {config.kind === 'normal' && (
          <>
            <label className={`field${primaryField === 'mean' ? ' field-primary' : ''}${isFieldDifferent('mean') ? ' field-different' : ''}`}>
              <span>Mean</span>
              <input
                ref={meanRef}
                type="number"
                value={localMean}
                onChange={(event) => setLocalMean(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'mean';
                  registerActiveInput(`${distribution.id}-mean`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-mean`);
                  updateConfig({ mean: parseNumber(localMean, 0) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 1)}
                placeholder="Enter mean"
                disabled={isBaseline}
              />
              {renderMainReference('mean')}
            </label>
            <label className={`field${isFieldDifferent('std') ? ' field-different' : ''}`}>
              <span>Std Dev</span>
              <input
                ref={stdRef}
                type="number"
                value={localStd}
                onChange={(event) => setLocalStd(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'std';
                  registerActiveInput(`${distribution.id}-std`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-std`);
                  updateConfig({ std: parseNumber(localStd, 1) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 2)}
                placeholder="Enter std dev"
                disabled={isBaseline}
              />
              {renderMainReference('std')}
            </label>
            <label className={`field${isFieldDifferent('min') ? ' field-different' : ''}`}>
              <span>Min</span>
              <input
                ref={normalMinRef}
                type="number"
                value={localNormalMin}
                onChange={(event) => setLocalNormalMin(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'normalMin';
                  registerActiveInput(`${distribution.id}-normalMin`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-normalMin`);
                  updateConfig({ min: localNormalMin === '' ? undefined : parseNumber(localNormalMin, 0) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 3)}
                placeholder="Optional"
                disabled={isBaseline}
              />
              {renderMainReference('min')}
            </label>
            <label className={`field${isFieldDifferent('max') ? ' field-different' : ''}`}>
              <span>Max</span>
              <input
                ref={normalMaxRef}
                type="number"
                value={localNormalMax}
                onChange={(event) => setLocalNormalMax(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'normalMax';
                  registerActiveInput(`${distribution.id}-normalMax`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-normalMax`);
                  updateConfig({ max: localNormalMax === '' ? undefined : parseNumber(localNormalMax, 0) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 4)}
                placeholder="Optional"
                disabled={isBaseline}
              />
              {renderMainReference('max')}
            </label>
          </>
        )}

        {config.kind === 'triangular' && (
          <>
            <label className={`field${isFieldDifferent('min') ? ' field-different' : ''}`}>
              <span>Min</span>
              <input
                ref={triMinRef}
                type="number"
                value={localTriMin}
                onChange={(event) => setLocalTriMin(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'triMin';
                  registerActiveInput(`${distribution.id}-triMin`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-triMin`);
                  updateConfig({ min: parseNumber(localTriMin, 0) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 1)}
                placeholder="Enter min"
                disabled={isBaseline}
              />
              {renderMainReference('min')}
            </label>
            <label className={`field${primaryField === 'mode' ? ' field-primary' : ''}${isFieldDifferent('mode') ? ' field-different' : ''}`}>
              <span>Mode</span>
              <input
                ref={triModeRef}
                type="number"
                value={localTriMode}
                onChange={(event) => setLocalTriMode(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'triMode';
                  registerActiveInput(`${distribution.id}-triMode`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-triMode`);
                  updateConfig({ mode: parseNumber(localTriMode, 0.5) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 2)}
                placeholder="Enter mode"
                disabled={isBaseline}
              />
              {renderMainReference('mode')}
            </label>
            <label className={`field${isFieldDifferent('max') ? ' field-different' : ''}`}>
              <span>Max</span>
              <input
                ref={triMaxRef}
                type="number"
                value={localTriMax}
                onChange={(event) => setLocalTriMax(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'triMax';
                  registerActiveInput(`${distribution.id}-triMax`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-triMax`);
                  updateConfig({ max: parseNumber(localTriMax, 1) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 3)}
                placeholder="Enter max"
                disabled={isBaseline}
              />
              {renderMainReference('max')}
            </label>
          </>
        )}

        {config.kind === 'uniform' && (
          <>
            <label className={`field${isFieldDifferent('min') ? ' field-different' : ''}`}>
              <span>Min</span>
              <input
                ref={uniMinRef}
                type="number"
                value={localUniMin}
                onChange={(event) => setLocalUniMin(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'uniMin';
                  registerActiveInput(`${distribution.id}-uniMin`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-uniMin`);
                  updateConfig({ min: parseNumber(localUniMin, 0) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 1)}
                placeholder="Enter min"
                disabled={isBaseline}
              />
              {renderMainReference('min')}
            </label>
            <label className={`field${isFieldDifferent('max') ? ' field-different' : ''}`}>
              <span>Max</span>
              <input
                ref={uniMaxRef}
                type="number"
                value={localUniMax}
                onChange={(event) => setLocalUniMax(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onFocus={() => {
                  focusedInputRef.current = 'uniMax';
                  registerActiveInput(`${distribution.id}-uniMax`);
                }}
                onBlur={() => {
                  focusedInputRef.current = null;
                  unregisterActiveInput(`${distribution.id}-uniMax`);
                  updateConfig({ max: parseNumber(localUniMax, 1) });
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, 2)}
                placeholder="Enter max"
                disabled={isBaseline}
              />
              {renderMainReference('max')}
            </label>
          </>
        )}
      </div>
    </div>
  );
};

